import { queryKeys, useHttpClient, useSession } from '@repo/api-client/react';
import {
  OrganizationsListResponseSchema,
  OrganizationSchema,
  type Organization,
  type OrganizationListItem,
} from '@repo/api-client/dtos';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getJSON, postJSON } from '../internal/http';
import { syncOrgsFromCache } from './sessionSync';

/**
 * List the *archived* orgs the current user is still a member of.
 *
 * Lives behind a separate cache key (`organizations.archived`) so it
 * never gets confused with the active list — restoring an org
 * surgically moves the row from this cache into the active list
 * cache, instead of the two views being projections of one shared
 * source of truth.
 */
export function useArchivedOrganizations() {
  const http = useHttpClient();

  const { data, error, isPending: loading, refetch } = useQuery({
    queryKey: queryKeys.organizations.archived(),
    queryFn: () => getJSON(http, '/orgs/archived', OrganizationsListResponseSchema),
    // Archived listing is a recovery surface, not a working surface.
    // It's fine to refetch on focus rather than caching aggressively.
    staleTime: 30 * 1000,
  });

  return {
    organizations: data?.items as OrganizationListItem[] | undefined,
    loading,
    error,
    refetch,
  };
}

/**
 * Restore a previously-archived org.
 *
 * On success we surgically reconcile both list caches:
 *
 *   - Active list (`organizations.all`) → prepend the restored row so
 *     the user sees it immediately at the top of their workspaces.
 *   - Archived list (`organizations.archived`) → drop the row, since
 *     the recovery list shouldn't keep showing something that's no
 *     longer archived.
 *
 * The detail row at `organizations.detail(id)` is also refreshed so
 * any open editing surface picks up the cleared `archivedAt`.
 *
 * The active-organization id is *not* changed here — switching is
 * the caller's job (see the screen using `useSetActiveOrganization`).
 */
export function useRestoreOrganization() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const { syncOrganizations } = useSession();

  const mutation = useMutation({
    mutationFn: (organizationId: string) =>
      // Body must be an object literal (`{}`), NOT `null`. NestJS sits
      // behind Express's `body-parser.json()` with its default
      // `strict: true`, which only accepts JSON arrays / objects at
      // the top level. Sending the literal `null` makes the parser
      // reject the request with `Unexpected token 'n', "null" is not
      // valid JSON` *before* the route handler runs — which the
      // client surfaces as an opaque ValidationError. The server
      // doesn't actually need a body here (the URL identifies the
      // org), but the wire still has to carry a parseable shape.
      postJSON(http, `/orgs/${organizationId}/restore`, {}, OrganizationSchema),
    onSuccess: (organization: Organization) => {
      queryClient.setQueryData(
        queryKeys.organizations.detail(organization.id),
        organization,
      );

      // The active list cache stores `OrganizationListItem`, which is
      // a subset of `Organization` plus an optional `stats` rollup.
      // Coerce by spreading and reusing the org's own fields — stats
      // come back empty until the next fetch cycle, which matches
      // what the server returns for newly-created orgs.
      queryClient.setQueryData<{ items: OrganizationListItem[] }>(
        queryKeys.organizations.all(),
        (cache) => {
          const restoredListItem = organization as unknown as OrganizationListItem;
          if (!cache) return { items: [restoredListItem] };
          // Defensive: drop any duplicate (shouldn't happen but caches
          // can drift across navigations) before prepending.
          const without = cache.items.filter((o) => o.id !== organization.id);
          return { ...cache, items: [restoredListItem, ...without] };
        },
      );

      queryClient.setQueryData<{ items: OrganizationListItem[] }>(
        queryKeys.organizations.archived(),
        (cache) =>
          cache
            ? { ...cache, items: cache.items.filter((o) => o.id !== organization.id) }
            : cache,
      );

      // Mirror the patched cache into the session projection so the
      // restored row is immediately switchable without a bootstrap.
      syncOrgsFromCache(queryClient, syncOrganizations);

      // Re-fetch in the background so server-truth stats / cursors land
      // before the user notices, but the optimistic patch above keeps
      // the UI snappy.
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.archived() });
    },
  });

  return {
    restoreOrganization: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
